import React, { useState } from "react";
import { FaCheck } from "react-icons/fa";
import { useSelector } from "react-redux";
import { selectIsBowlingRestaurant } from "../../store/setupApi";
import "./css/fullScreenOnboarding.css";
import translations from "./translations.json";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function FullScreenOnboarding(props) {
  const isBowling = useSelector(selectIsBowlingRestaurant);
  const {
    initialEmail = "",
    initialRestaurantName = "",
    saving = false,
    error = "",
    onSave,
    brand = {
      logo: isBowling
        ? "https://static.reservaties.net/images/logo/HappyBowlingFinal.png"
        : "https://static.reservaties.net/images/logo/HappyChef.png",
      tagline: "", // will fallback to translations.tagline
    },
  } = props;

  const preferredLanguage = localStorage.getItem("preferredLanguage") || "nl";
  const t =
    translations?.[preferredLanguage]?.onboarding ||
    translations?.nl?.onboarding;

  // sanitize incoming
  const initialEmailStr =
    typeof initialEmail === "string"
      ? initialEmail
      : initialEmail?.toString?.() || "";
  const initialNameStr =
    typeof initialRestaurantName === "string"
      ? initialRestaurantName
      : initialRestaurantName?.toString?.() || "";

  const [email, setEmail] = useState(initialEmailStr);
  const [restaurantName, setRestaurantName] = useState(initialNameStr);
  const [touched, setTouched] = useState(false);

  const emailStr = typeof email === "string" ? email : "";
  const nameStr = typeof restaurantName === "string" ? restaurantName : "";

  const emailValid = EMAIL_RE.test(emailStr.trim());
  const restaurantValid = nameStr.trim().length > 1;
  const canSave =
    emailValid && restaurantValid && !saving && typeof onSave === "function";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSave) return;
    await onSave({
      email: emailStr.trim(),
      restaurantName: nameStr.trim(),
    });
  };

  return (
    <div
      className="full-screen-onboarding"
      role="dialog"
      aria-modal="true"
      aria-labelledby="full-screen-onboarding-title"
    >
      <div className="full-screen-onboarding__card">
        {/* LEFT column: branding */}
        <div className="full-screen-onboarding__branding-panel">
          <div className="full-screen-onboarding__branding-inner">
            <div className="full-screen-onboarding__logo-wrapper">
              <img
                src={brand.logo}
                alt="HappyChef"
                className="full-screen-onboarding__brand-logo"
                loading="eager"
              />
            </div>

            <h2
              id="full-screen-onboarding-title"
              className="full-screen-onboarding__title"
            >
              {t.title}
            </h2>
            <p className="full-screen-onboarding__tagline">
              {brand.tagline || t.tagline}
            </p>

            <div className="full-screen-onboarding__features">
              <div className="full-screen-onboarding__feature-item">
                <div className="full-screen-onboarding__feature-icon">
                  <FaCheck size={16} />
                </div>
                <div className="full-screen-onboarding__feature-text">
                  {t.bullets?.[0]}
                </div>
              </div>
              <div className="full-screen-onboarding__feature-item">
                <div className="full-screen-onboarding__feature-icon">
                  <FaCheck size={16} />
                </div>
                <div className="full-screen-onboarding__feature-text">
                  {t.bullets?.[1]}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT column: form */}
        <div className="full-screen-onboarding__form-panel">
          <div className="full-screen-onboarding__form-container">
            {error ? (
              <div className="full-screen-onboarding__error-message">
                {error || t.errors.generic}
              </div>
            ) : null}

            <form
              onSubmit={handleSubmit}
              className="full-screen-onboarding__form"
              noValidate
            >
              {/* Email */}
              <div className="full-screen-onboarding__form-group">
                <label
                  className="full-screen-onboarding__label"
                  htmlFor="full-screen-onboarding-email"
                >
                  {t.form.email.label}
                </label>
                <input
                  id="full-screen-onboarding-email"
                  className={`full-screen-onboarding__input ${
                    touched && !emailValid
                      ? "full-screen-onboarding__input--error"
                      : ""
                  }`}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.form.email.placeholder}
                  autoFocus
                  required
                  autoComplete="email"
                />
                {touched && !emailValid && (
                  <span className="full-screen-onboarding__field-hint">
                    {t.form.email.hintInvalid}
                  </span>
                )}
              </div>

              {/* Restaurant name */}
              <div className="full-screen-onboarding__form-group">
                <label
                  className="full-screen-onboarding__label"
                  htmlFor="full-screen-onboarding-restaurant"
                >
                  {t.form.restaurant.label}
                </label>
                <input
                  id="full-screen-onboarding-restaurant"
                  className={`full-screen-onboarding__input ${
                    touched && !restaurantValid
                      ? "full-screen-onboarding__input--error"
                      : ""
                  }`}
                  type="text"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  placeholder={t.form.restaurant.placeholder}
                  required
                  autoComplete="organization"
                />
                {touched && !restaurantValid && (
                  <span className="full-screen-onboarding__field-hint">
                    {t.form.restaurant.hintInvalid}
                  </span>
                )}
              </div>

              <button
                className="full-screen-onboarding__primary-button"
                type="submit"
                disabled={!canSave}
              >
                {saving ? t.buttons.saving : t.buttons.save}
              </button>

              {/* No skip / close => obligatory */}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
